/**
 * <cart-panel>
 *
 * Top-level controller for the cart slide-in panel.
 * Owns: open/close, focus trap, body-scroll lock, section refresh,
 * sub-drawer trigger delegation, share-cart restore on URL load,
 * recommendation quick-add.
 *
 * Mirrors the role of <cart-drawer> in cart-drawer.js but is independent —
 * the legacy drawer continues to work when settings.cart_type === 'drawer'.
 *
 * Reused craft globals: trapFocus, removeTrapFocus, subscribe,
 * PUB_SUB_EVENTS, fetchConfig, window.routes, window.cartStrings.
 */
class CartPanel extends HTMLElement {
  constructor() {
    super();

    this._cartUpdateUnsubscriber = undefined;
    this._activeElement = null;
    this._sharedCartRestoreAttempted = false;
    this._addingFromRecs = false;

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });

    const overlay = this.querySelector('#CartPanel-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    const closeBtn = this.querySelector('.cart-panel__close');
    if (closeBtn) closeBtn.addEventListener('click', this.close.bind(this));

    // Click on the host element OUTSIDE the inner box also closes (the host
    // covers the full viewport while open; anything not inside .cart-panel__inner
    // is "outside the panel").
    this.addEventListener('mousedown', (event) => {
      if (!this.classList.contains('active')) return;
      const inner = this.querySelector('.cart-panel__inner');
      if (inner && !inner.contains(event.target)) {
        this.close();
      }
    });

    // Click outside an open sub-drawer (but inside the inner) closes the
    // sub-drawer. Exclude clicks on the action-bar triggers (they have their
    // own toggle logic) and inputs/buttons inside any open sub-drawer.
    this.addEventListener('mousedown', (event) => {
      const open = this.querySelector('cart-panel-subdrawer.is-open');
      if (!open) return;
      if (open.contains(event.target)) return;
      if (event.target.closest('[data-subdrawer-trigger]')) return;
      if (event.target.closest('[data-subdrawer-close]')) return;
      open.close();
    });

    // Sub-drawer trigger delegation
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-subdrawer-trigger]');
      if (trigger) {
        event.preventDefault();
        this.openSubdrawer(trigger.dataset.subdrawerTrigger);
        return;
      }
      const closer = event.target.closest('[data-subdrawer-close]');
      if (closer) {
        event.preventDefault();
        this.closeAllSubdrawers();
      }
    });

    // Recommendation quick-add (single-variant products) — plain button, JS-driven add
    this.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-cart-panel-add]');
      if (btn && btn.tagName === 'BUTTON') {
        event.preventDefault();
        this._handleRecAdd(btn);
      }
    });

    // View Cart / Checkout: both navigate away. Toggle a transient loading
    // state on click so the user sees immediate feedback during the
    // pre-navigation gap (browser hasn't started fetching the new page yet).
    // Don't preventDefault — we WANT the native navigation/submit to proceed.
    this.addEventListener('click', (event) => {
      const cta = event.target.closest('[data-cart-panel-navigate], [data-cart-panel-checkout]');
      if (!cta) return;
      if (cta.disabled || cta.getAttribute('aria-disabled') === 'true') return;
      cta.classList.add('loading');
      cta.setAttribute('aria-busy', 'true');
    });

    // Order-note Save button. Force-flush the note save (the embedded
    // <cart-note> auto-saves on `change` with a 300ms debounce, which can
    // race with the user clicking Save → previously the subdrawer closed
    // before the debounced fetch fired and the note never persisted). Wait
    // for the response before closing.
    this.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-cart-note-save]');
      if (btn) {
        event.preventDefault();
        this._handleNoteSave(btn);
      }
    });

    // Intercept the note textarea's `change` event in the capture phase so
    // we run our own save (with `sections` + renderContents) instead of
    // letting <cart-note>'s plain bubble-phase auto-save fire. The base
    // auto-save POSTs `note` only — no sections — so the action-bar's
    // `Order note (1)` count stays stale: server cart.note flips, panel UI
    // doesn't. stopPropagation prevents the duplicate POST from cart-note;
    // our debounced wrapper preserves the auto-save UX (typing + closing
    // the subdrawer without clicking Save still persists) while keeping
    // the counter and any discount/footer state in sync via renderContents.
    // Inline debounce with a `cancel` method — the shared global.js
    // debounce doesn't expose one, and we need to clear a pending
    // auto-save when the user clicks the explicit Save button so the
    // same value isn't POSTed twice (auto-save + explicit save).
    let noteAutoSaveTimer;
    const noteAutoSave = (value) => {
      clearTimeout(noteAutoSaveTimer);
      noteAutoSaveTimer = setTimeout(() => this._saveNoteAndRender(value), ON_CHANGE_DEBOUNCE_TIMER);
    };
    noteAutoSave.cancel = () => clearTimeout(noteAutoSaveTimer);
    this._debouncedNoteAutoSave = noteAutoSave;

    this.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!target || target.id !== 'CartPanel-Note') return;
        event.stopPropagation();
        this._debouncedNoteAutoSave(target.value);
      },
      true /* capture: fire before cart-note's bubble-phase listener */
    );

    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (this._cartUpdateUnsubscriber) return;
    this._cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'cart-panel') return;
      this.refreshSections();
    });

    // bfcache reset. iOS Safari (and now Chrome / Firefox) keep the entire
    // page including JS state in the back-forward cache. When the user
    // clicks View Cart / Checkout the navigation pauses the page WITH the
    // CTA still in `.loading` state; pressing Back restores it as-is, so
    // the button reads as still loading even though the navigation that
    // was in flight is over. Same for any rec-add `.is-loading` /
    // `.is-success`, discount `.loading`, save-note `.loading`, etc. that
    // happened to be mid-flight when navigation began. `pageshow` with
    // `event.persisted === true` is the canonical signal for bfcache
    // restore — clear transient states there. Don't close the panel; the
    // user came back to it, leave it as they had it.
    if (!this._pageshowListener) {
      this._pageshowListener = (event) => {
        if (event.persisted) this._clearTransientStates();
      };
      window.addEventListener('pageshow', this._pageshowListener);
    }

    // One-shot: restore a shared cart when the URL contains share_cart:true
    if (!this._sharedCartRestoreAttempted) {
      this._sharedCartRestoreAttempted = true;
      this._maybeRestoreSharedCart();
    }
  }

  disconnectedCallback() {
    if (this._cartUpdateUnsubscriber) {
      this._cartUpdateUnsubscriber();
      this._cartUpdateUnsubscriber = undefined;
    }
    if (this._pageshowListener) {
      window.removeEventListener('pageshow', this._pageshowListener);
      this._pageshowListener = undefined;
    }
  }

  /**
   * Reset every transient loading / success class inside the panel. Called
   * on bfcache restore so a navigation that was in flight when the page
   * paused doesn't leave buttons stuck in their click-state.
   */
  _clearTransientStates() {
    const navCtas = this.querySelectorAll('.cart-panel__view-cart, .cart-panel__checkout');
    navCtas.forEach((el) => {
      el.classList.remove('loading');
      el.removeAttribute('aria-busy');
    });

    const recBtns = this.querySelectorAll('[data-cart-panel-add]');
    recBtns.forEach((el) => {
      el.classList.remove('is-loading', 'is-success');
      el.removeAttribute('aria-disabled');
    });

    const noteBtn = this.querySelector('[data-cart-note-save]');
    if (noteBtn) {
      noteBtn.classList.remove('loading');
      noteBtn.disabled = false;
    }

    const discountApply = this.querySelector('[data-discount-apply]');
    if (discountApply) {
      discountApply.classList.remove('loading', 'is-success');
      discountApply.disabled = false;
    }

    this.querySelectorAll('[data-discount-remove]').forEach((el) => {
      el.classList.remove('loading');
      el.disabled = false;
    });
  }

  /* ---------------------------------------------------------------- *
   *  Interface compatibility with <cart-drawer> / <cart-notification>
   *  so PDP <product-form> (product-form.js) can drive the panel
   *  exactly the way it drives the legacy drawer:
   *    - setActiveElement(el)
   *    - getSectionsToRender()
   *    - renderContents(parsedState)
   * ---------------------------------------------------------------- */

  setActiveElement(element) {
    this._activeElement = element;
  }

  /**
   * Sections to refetch when the cart changes from outside the panel
   * (e.g. PDP add-to-cart). Section IDs match the section name *and*
   * the wrapping `<div id="shopify-section-…">` id, so the existing
   * product-form code (which does `document.getElementById(section.id)`
   * / `document.querySelector(section.selector)`) finds the right
   * targets.
   */
  getSectionsToRender() {
    return [
      { id: 'cart-panel',       selector: 'cart-panel' },
      { id: 'cart-icon-bubble', selector: '#cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  /**
   * Called by product-form.js after a successful PDP add-to-cart.
   * Replace the relevant cart-panel regions surgically (don't blow
   * away the host element so subscribers stay attached) and open
   * the panel so the customer sees their item.
   */
  renderContents(parsedState) {
    if (!parsedState) return;

    // Capture the empty-state at entry so we can detect transitions in either
    // direction. Doing this before we touch any classes is important — the
    // non-empty → empty transition (last item removed) was previously falling
    // into the surgical region-swap branch, which couldn't render the
    // empty-state markup (`cart-panel__main--empty`) because that branch
    // only swaps `cart-panel-items` / footer / recommendations regions.
    // Result: the form stayed in the DOM and the empty UI never appeared.
    const wasEmpty = this.classList.contains('is-empty');

    // Refresh both panel and bubble from the same cart-panel section payload
    // (faster than refreshSections's two-fetch path because we already have
    // the response).
    const sections = parsedState.sections || {};
    if (sections['cart-panel']) {
      const newPanelDoc = new DOMParser().parseFromString(sections['cart-panel'], 'text/html');
      const newPanel = newPanelDoc.querySelector('cart-panel');
      if (newPanel) {
        const willBeEmpty = newPanel.classList.contains('is-empty');
        this.classList.toggle('is-empty', willBeEmpty);
        this.dataset.hasRecs = newPanel.dataset.hasRecs || 'false';

        const newTitle = newPanelDoc.querySelector('.cart-panel__title');
        const curTitle = this.querySelector('.cart-panel__title');
        if (newTitle && curTitle) curTitle.innerHTML = newTitle.innerHTML;

        // Both empty<->non-empty transitions need a whole-body swap because
        // the two branches render completely different subtrees: the empty
        // branch is `<div class="cart-panel__main--empty">` and the
        // non-empty branch is the full `<form>` with items / footer / recs
        // / action-bar / subdrawers. The surgical region swap below can't
        // bridge those — the regions don't exist in the empty branch's HTML.
        const newBody = newPanelDoc.querySelector('.cart-panel__body');
        const curBody = this.querySelector('.cart-panel__body');
        if (wasEmpty !== willBeEmpty && newBody && curBody) {
          curBody.innerHTML = newBody.innerHTML;
        } else {
          ['cart-panel-items', 'cart-panel-footer', 'cart-panel-recommendations'].forEach((region) => {
            const next = newPanelDoc.querySelector(`[data-region="${region}"]`);
            const cur = this.querySelector(`[data-region="${region}"]`);
            if (next && cur) cur.replaceWith(next);
            if (next && !cur) {
              const body = this.querySelector('.cart-panel__body');
              if (body) body.appendChild(next);
            }
            if (!next && cur) cur.remove();
          });

          // Swap the action-bar so its `Order note (1)` / `Discount(N)` count
          // labels stay in sync. The bar's button click bindings are
          // delegated from the host, so swapping the element doesn't break
          // them. We don't replace it on the empty<->non-empty transition
          // path (handled by the whole-body innerHTML swap above).
          //
          // Preserve keyboard focus across the swap: if the user is currently
          // tabbed into one of the action-bar triggers and we swap the bar,
          // the focused button gets detached and focus dumps onto the panel
          // container. Identify the focused trigger before swap, then move
          // focus to its replacement in the new bar so tab navigation stays
          // intact for keyboard / screen-reader users.
          const newBar = newPanelDoc.querySelector('.cart-panel__action-bar');
          const curBar = this.querySelector('.cart-panel__action-bar');
          if (newBar && curBar) {
            const focusedTrigger = curBar.contains(document.activeElement)
              ? document.activeElement.closest('[data-subdrawer-trigger]')
              : null;
            const focusedKey = focusedTrigger && focusedTrigger.dataset.subdrawerTrigger;
            curBar.replaceWith(newBar);
            if (focusedKey) {
              const replacement = newBar.querySelector(`[data-subdrawer-trigger="${focusedKey}"]`);
              if (replacement) {
                try { replacement.focus({ preventScroll: true }); } catch (_) { /* ignore */ }
              }
            }
          }
        }
      }
    }
    if (sections['cart-icon-bubble']) {
      const bubbleDoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const bubble = document.getElementById('cart-icon-bubble');
      const bubbleSrc = bubbleDoc.querySelector('.shopify-section, body');
      if (bubble && bubbleSrc) bubble.innerHTML = bubbleSrc.innerHTML;
    }

    // Defer open so DOM swaps settle first
    setTimeout(() => this.open(this._activeElement));
  }

  /* ---------------------------------------------------------------- */

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    // On the /cart page itself the panel adds nothing — the user is
    // already looking at the full cart. Leave the icon as the default
    // anchor (no panel hijack, no role swap, no preventDefault) so
    // clicking it is a no-op (it's `<a href="/cart">`, already on /cart).
    if (this._isOnCartPage()) return;
    // Guard against duplicate listeners — section reloads (theme editor,
    // multi-section refreshes, hot reload during dev) construct a fresh
    // <cart-panel>, and the cart-icon-bubble lives in a different section
    // that persists, so each new construction would otherwise stack open()
    // bindings on the same icon. The `dataset` flag survives our scope
    // because it lives on the persistent icon element, not on `this`.
    if (cartLink.dataset.cartPanelWired === 'true') return;
    cartLink.dataset.cartPanelWired = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE' || event.code === 'Enter') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  _isOnCartPage() {
    // Match `/cart`, `/cart/`, locale-prefixed `/{locale}/cart`. Trailing
    // query strings or hashes are fine — we only care that the path is
    // the cart route.
    const path = window.location.pathname.replace(/\/+$/, '');
    return path === '/cart' || /\/[a-z]{2}(-[a-zA-Z]{2})?\/cart$/.test(path);
  }

  open(triggeredBy) {
    if (triggeredBy) this._activeElement = triggeredBy;

    // Reliable slide-in. The previous `setTimeout(0)` deferred the class
    // add by one task, but on first open with a busy main thread (defer
    // scripts running, 3rd-party JS hammering CPU) the browser would
    // collapse the visibility:hidden→visible flip and the
    // translateX(100%)→0 transform change into a single paint frame —
    // skipping interpolation entirely. The result: panel "appears
    // suddenly" with no animation. Subsequent opens worked because the
    // panel had been laid out by then.
    //
    // Fix: force the from-state to commit by reading layout (`offsetWidth`),
    // then add the active class on the next animation frame so the browser
    // has a guaranteed paint of the start state before the transition.
    const inner = this.querySelector('.cart-panel__inner');
    if (inner) {
      // Layout read forces a style/layout flush — the browser computes
      // the current `transform: translateX(100%)` and any deferred work.
      // The `void` is just to satisfy lints that flag pure-side-effect
      // expressions; the read is what matters.
      void inner.offsetWidth;
    }
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const innerEl = this.querySelector('.cart-panel__inner');
        const focusEl = innerEl ? innerEl : this.querySelector('.cart-panel__close');
        if (focusEl && typeof trapFocus === 'function') {
          trapFocus(this, focusEl);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.closeAllSubdrawers();
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this._activeElement);
    }
    document.body.classList.remove('overflow-hidden');
  }

  openSubdrawer(name) {
    const sd = this.querySelector(`cart-panel-subdrawer[data-subdrawer="${name}"]`);
    if (sd && typeof sd.open === 'function') sd.open();
  }

  closeAllSubdrawers() {
    this.querySelectorAll('cart-panel-subdrawer').forEach((sd) => {
      if (typeof sd.close === 'function') sd.close();
    });
  }

  /**
   * Refresh the panel + cart icon bubble via Section Rendering API.
   * Surgical — only swaps regions, keeps open sub-drawers intact.
   */
  refreshSections() {
    return fetch(`${routes.cart_url}?section_id=cart-panel`)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // Toggle empty state class on outer host
        const newPanel = doc.querySelector('cart-panel');
        const willBeEmpty = newPanel ? newPanel.classList.contains('is-empty') : false;
        const wasEmpty = this.classList.contains('is-empty');
        if (newPanel) {
          this.classList.toggle('is-empty', willBeEmpty);
          this.dataset.hasRecs = newPanel.dataset.hasRecs || 'false';
        }
        // Refresh the count badge in the panel header (lives outside [data-region]s)
        const newTitle = doc.querySelector('.cart-panel__title');
        const curTitle = this.querySelector('.cart-panel__title');
        if (newTitle && curTitle) curTitle.innerHTML = newTitle.innerHTML;

        // The empty branch and the non-empty branch render completely
        // different body subtrees (`.cart-panel__main--empty` vs `<form>`),
        // so on transition we swap the whole body innerHTML. Otherwise stick
        // to surgical region swaps to preserve any open sub-drawer state.
        if (wasEmpty !== willBeEmpty) {
          const newBody = doc.querySelector('.cart-panel__body');
          const curBody = this.querySelector('.cart-panel__body');
          if (newBody && curBody) curBody.innerHTML = newBody.innerHTML;
        } else {
          ['cart-panel-items', 'cart-panel-footer', 'cart-panel-recommendations'].forEach(
            (region) => {
              const next = doc.querySelector(`[data-region="${region}"]`);
              const cur = this.querySelector(`[data-region="${region}"]`);
              if (next && cur) cur.replaceWith(next);
              if (next && !cur) {
                if (region === 'cart-panel-recommendations') {
                  const body = this.querySelector('.cart-panel__body');
                  if (body) body.appendChild(next);
                }
              }
              if (!next && cur) cur.remove();
            }
          );

          // Refresh the action bar's count labels (`Order note (1)`,
          // `Discount (N)`) — the labels are server-rendered from
          // `cart.note != blank` and the cart-level discount count, so
          // they get out of sync after a note save / discount apply if
          // we don't swap the bar. Skip the action-bar's children
          // beyond the labels so the subdrawer triggers' click bindings
          // (delegated from `<cart-panel>`) keep working — those
          // delegate from the host so element identity doesn't matter.
          //
          // Preserve focus on the active trigger across the swap (see the
          // matching block in renderContents).
          const newBar = doc.querySelector('.cart-panel__action-bar');
          const curBar = this.querySelector('.cart-panel__action-bar');
          if (newBar && curBar) {
            const focusedTrigger = curBar.contains(document.activeElement)
              ? document.activeElement.closest('[data-subdrawer-trigger]')
              : null;
            const focusedKey = focusedTrigger && focusedTrigger.dataset.subdrawerTrigger;
            curBar.replaceWith(newBar);
            if (focusedKey) {
              const replacement = newBar.querySelector(`[data-subdrawer-trigger="${focusedKey}"]`);
              if (replacement) {
                try { replacement.focus({ preventScroll: true }); } catch (_) { /* ignore */ }
              }
            }
          }
        }

        // Refresh the header cart icon bubble independently
        return fetch(`${routes.cart_url}?section_id=cart-icon-bubble`)
          .then((r) => r.text())
          .then((bubbleHtml) => {
            const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
            const bubble = document.getElementById('cart-icon-bubble');
            const bubbleSrc = bubbleDoc.querySelector('.shopify-section');
            if (bubble && bubbleSrc) {
              bubble.innerHTML = bubbleSrc.innerHTML;
            }
          });
      })
      .catch((e) => {
        console.error('cart-panel: refreshSections failed', e);
      });
  }

  /** ------------------- Order note save ------------------- */
  /**
   * Save the textarea content to cart.note synchronously (waits on the
   * fetch before closing the subdrawer). This is the user-explicit save
   * path; the embedded <cart-note>'s debounced `change` listener still
   * handles auto-save on blur. We don't try to cancel the debounce here —
   * an extra POST with the same value is harmless and simpler than
   * reaching into the debounced closure.
   */
  _handleNoteSave(btn) {
    if (btn.disabled) return;
    const sd = btn.closest('cart-panel-subdrawer');
    const textarea = sd?.querySelector('textarea[name="note"]');
    if (!sd || !textarea) return;

    // Pre-empt any pending debounced auto-save for this same value so the
    // explicit Save isn't shadowed by a delayed auto-save firing right
    // after we've already rendered the response.
    if (this._debouncedNoteAutoSave && this._debouncedNoteAutoSave.cancel) {
      this._debouncedNoteAutoSave.cancel();
    }

    btn.classList.add('loading');
    btn.disabled = true;

    this._saveNoteAndRender(textarea.value)
      .then((result) => {
        if (result && result.ok) sd.close();
      })
      .finally(() => {
        btn.classList.remove('loading');
        btn.disabled = false;
      });
  }

  /**
   * Shared note-save core: POST /cart/update.js with `sections`, render
   * the response in-place (action-bar count syncs because renderContents
   * swaps `.cart-panel__action-bar`), publish cartUpdate on success or
   * cartError on failure. Single fetch.
   *
   * Used by both the explicit Save button and the textarea's debounced
   * auto-save. The auto-save needs the full render path because the base
   * <cart-note>'s plain `{note}` POST doesn't refresh the action-bar count.
   */
  _saveNoteAndRender(noteValue) {
    const cartErrors = document.getElementById('CartPanel-CartErrors');
    if (cartErrors) cartErrors.textContent = '';

    const sectionsToRender = this.getSectionsToRender().map((s) => s.id).join(',');
    const body = JSON.stringify({
      note: noteValue,
      sections: sectionsToRender,
      sections_url: window.location.pathname,
    });

    return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body })
      .then((r) => r.json())
      .then((data) => {
        if (data && data.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cart-panel',
            errors: data.errors || data.description,
            message: data.message,
          });
          if (cartErrors && window.cartStrings && window.cartStrings.error) {
            cartErrors.textContent = window.cartStrings.error;
          }
          return { ok: false, data };
        }
        this.renderContents(data);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-panel', cartData: data });
        return { ok: true, data };
      })
      .catch((e) => {
        console.error('cart-panel: note save failed', e);
        if (cartErrors && window.cartStrings && window.cartStrings.error) {
          cartErrors.textContent = window.cartStrings.error;
        }
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'cart-panel',
          message: (e && e.message) || 'Network error',
        });
        return { ok: false };
      });
  }

  /** ------------------- Recommendation quick-add ------------------- */
  _handleRecAdd(btn) {
    if (this._addingFromRecs) return;
    if (btn.disabled) return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    this._addingFromRecs = true;

    btn.classList.add('is-loading');
    btn.setAttribute('aria-disabled', 'true');

    const clearLoading = () => {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-disabled');
    };

    // Mirror product-form.js: ask /cart/add.js to return the rendered
    // sections inline, then hand the response straight to renderContents.
    // This is one fetch instead of three (the previous version paid an
    // extra refreshSections round-trip for the same two sections we
    // already had in the add response — the dominant cause of "stuck"
    // recs adds when the network was slow or Cloudflare paused requests).
    const sections = this.getSectionsToRender().map((s) => s.id).join(',');
    const body = JSON.stringify({
      items: [{ id: parseInt(variantId, 10), quantity: 1 }],
      sections,
      sections_url: window.location.pathname,
    });

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body,
    })
      .then((r) => r.json())
      .then((data) => {
        // Shopify returns a `status` field only on errors (422 quantity
        // limit, 404 sold-out, etc). Treat any truthy status the same way
        // product-form.js does: surface via cartError, reset the button,
        // skip the success animation and the section render.
        if (data.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cart-panel',
            productVariantId: variantId,
            errors: data.errors || data.description,
            message: data.message,
          });
          clearLoading();
          return;
        }

        // Render the response sections first (this swaps the recs region,
        // detaching `btn` and replacing it with a fresh server-rendered
        // copy that doesn't carry `.is-success`). Then re-apply the success
        // state to whichever button now represents this variant.
        //
        // Previously we set `.is-success` on the old `btn` *before*
        // renderContents. Both happened in the same JS task, so the browser
        // never painted the success state — only the post-swap fresh button
        // (without the class) reached the screen, and the tick was invisible.
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-panel',
          productVariantId: variantId,
          cartData: data,
        });

        this.renderContents(data);

        const successBtn =
          this.querySelector(`[data-cart-panel-add][data-variant-id="${variantId}"]`) || btn;
        successBtn.classList.remove('is-loading');
        successBtn.classList.add('is-success');
        successBtn.setAttribute('aria-disabled', 'true');
        setTimeout(() => {
          // Re-resolve at clear time too — recs could have been swapped again
          // by an unrelated cartUpdate (e.g. share subscriber or a fast
          // follow-up action). If the variant button no longer exists, the
          // server-rendered fresh button never had .is-success, nothing to do.
          const stillThere =
            this.querySelector(`[data-cart-panel-add][data-variant-id="${variantId}"]`) || successBtn;
          stillThere.classList.remove('is-success');
          stillThere.removeAttribute('aria-disabled');
        }, 1100);
      })
      .catch((e) => {
        // Network failure / aborted fetch / non-JSON body. Without this
        // cleanup the button would stay in `is-loading` forever (the prior
        // implementation only cleared the loading state inside the success
        // branches, so a Cloudflare pause or offline blip wedged the UI).
        console.error('cart-panel: rec add failed', e);
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'cart-panel',
          productVariantId: variantId,
          message: (e && e.message) || 'Network error',
        });
        clearLoading();
      })
      .finally(() => {
        this._addingFromRecs = false;
      });
  }

  /** ------------------- Share-cart restore ------------------- */
  _maybeRestoreSharedCart() {
    const search = window.location.search;
    if (!search || search.indexOf('share_cart:true') === -1) return;

    const items = search
      .substring(1)
      .split('&')
      .reduce((acc, param) => {
        if (!param.startsWith('id:')) return acc;
        const [idPart, qPart] = param.split(',');
        const id = parseInt(idPart.slice(3), 10);
        const quantity = parseInt((qPart || 'q:1').slice(2), 10) || 1;
        if (Number.isFinite(id)) acc.push({ id, quantity });
        return acc;
      }, []);
    if (items.length === 0) return;

    // Scrub the share params from the URL bar before fetching so a reload
    // mid-flight doesn't re-trigger restore (which would double quantities)
    // and so the recipient doesn't accidentally re-share the same URL.
    try {
      history.replaceState(null, '', window.location.pathname);
    } catch (_) { /* ignore */ }

    // Pass `sections` so /cart/add.js returns the rendered HTML inline —
    // single fetch instead of the previous 1 + 2 (refreshSections fired
    // /cart?section_id=cart-panel + /cart?section_id=cart-icon-bubble for
    // the same data we'd already have in the response).
    const sections = this.getSectionsToRender().map((s) => s.id).join(',');

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data && data.status) {
          // Soft / partial failure (e.g., one of the variants is sold out
          // since the link was sent). Surface a visible error in the panel
          // and publish cartError so other subscribers can react. Still
          // open the panel so the customer sees whatever did land.
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cart-panel-share',
            errors: data.errors || data.description,
            message: data.message,
          });
          const errors = document.getElementById('CartPanel-CartErrors');
          if (errors) errors.textContent = data.description || data.message || (window.cartStrings && window.cartStrings.error) || '';
          this.open();
          return;
        }
        this.renderContents(data);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-panel', cartData: data });
        this.open();
      })
      .catch((e) => {
        // Network failure / non-JSON body. Surface a visible error so the
        // recipient knows the link didn't fully load — without this they'd
        // land on the homepage with whatever cart they had before, no
        // indication that the share link tried and failed.
        console.error('cart-panel: shared cart restore failed', e);
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'cart-panel-share',
          message: (e && e.message) || 'Network error',
        });
        const errors = document.getElementById('CartPanel-CartErrors');
        if (errors && window.cartStrings && window.cartStrings.error) {
          errors.textContent = window.cartStrings.error;
        }
        this.open();
      });
  }
}

customElements.define('cart-panel', CartPanel);

/* ============================================================================
   Global cart API for third-party apps (Fast Bundle, etc.)

   Third-party apps perform their own /cart/add and don't know about the
   theme's <cart-panel> custom element directly. They asked for two globals
   they can call after a successful cart-add so the drawer updates + opens
   the same way it does for the theme's native PDP ATC.

   Both functions are no-ops if <cart-panel> isn't on the page (e.g. inside
   an iframe / admin preview / unsupported template) so they're safe to
   call unconditionally.
   ============================================================================ */

/**
 * Refresh the cart drawer's markup (item list, footer totals, header bubble).
 *
 * @param {Object} [parsedState] Optional. The parsed JSON response from a
 *        `POST /cart/add.js` (or `/cart/change.js` / `/cart/update.js`) call
 *        that was made with the form fields:
 *          sections      = 'cart-panel,cart-icon-bubble'
 *          sections_url  = window.location.pathname
 *        The shape we read is:
 *          {
 *            sections: {
 *              'cart-panel':       '<html string for the cart-panel section>',
 *              'cart-icon-bubble': '<html string for the cart icon bubble>',
 *            },
 *            ...other standard cart fields (item_count, items, etc.)
 *          }
 *        Pass it when you already have it — it's the fast path with no
 *        extra network round-trip.
 *
 *        When `parsedState` is omitted, `null`, or missing `sections`, we
 *        fall back to `<cart-panel>.refreshSections()`, which does a
 *        single `GET {cart_url}?section_id=cart-panel` and swaps the
 *        drawer's body. Slower (one round-trip) but no caller setup
 *        required.
 *
 * @returns {Promise<void>} Resolves once the markup has been applied.
 *
 * No-op safe: if `<cart-panel>` isn't on the page (e.g. checkout/password
 * templates), the call returns immediately without throwing.
 *
 * Usage from third-party JS:
 *   // Fast path — you already have the response:
 *   await window.refreshCart(myAddResponse);
 *   // Fallback — no response on hand:
 *   await window.refreshCart();
 */
window.refreshCart = async function (parsedState) {
  const panel = document.querySelector('cart-panel');
  if (!panel) return;

  // Fast path — caller (e.g. Fast Bundle) already has the /cart/add.js
  // sections-included response. Apply it in place. On any throw, fall through
  // to the slower-but-safer refetch path below instead of rejecting.
  if (
    parsedState &&
    parsedState.sections &&
    typeof parsedState.sections === 'object' &&
    typeof panel.renderContents === 'function'
  ) {
    try {
      panel.renderContents(parsedState);
      return;
    } catch (e) {
      console.warn('[cart] refreshCart fast path failed; falling back to refetch', e);
    }
  }

  // Fallback — no response on hand (or fast path threw). Use the cart-panel's
  // own section-fetch path (it refetches `?section_id=cart-panel` and swaps
  // the markup). Server-side cart state is already updated by the caller; if
  // this fails, the drawer markup is stale but the cart itself is correct.
  if (typeof panel.refreshSections === 'function') {
    try {
      await panel.refreshSections();
    } catch (e) {
      console.warn('[cart] refreshCart fallback failed; drawer markup may be stale', e);
    }
  }
};

/**
 * Open the cart drawer with the theme's slide-in animation + focus trap.
 *
 * @param {Element} [triggeredBy] Optional. The element that triggered the
 *        open (typically the Add-to-cart button). Used to restore focus to
 *        that element when the drawer is later closed. Pass `null` /
 *        `undefined` if you don't care about focus restoration.
 *
 * @returns {void} The drawer's slide-in animation runs asynchronously; this
 *          function returns immediately after queuing it.
 *
 * No-op safe: if `<cart-panel>` isn't on the page (e.g. checkout/password
 * templates), the call returns immediately without throwing.
 *
 * Usage from third-party JS:
 *   // Open without focus tracking:
 *   window.openCart();
 *   // Open + remember the trigger so focus returns on close:
 *   window.openCart(buttonElement);
 */
window.openCart = function (triggeredBy) {
  const panel = document.querySelector('cart-panel');
  if (!panel || typeof panel.open !== 'function') return;
  try {
    panel.open(triggeredBy);
  } catch (e) {
    console.warn('[cart] openCart failed', e);
  }
};
